import ContentIllustration from '../../components/ui/ContentIllustration';
import { useState, useEffect } from 'react';
import useCustomStore from '../../store/customStore';
import { Loader2, Clock, AlertTriangle, CheckCircle, Share2, RefreshCw, Eye, Trash2, Layers, Plus, Copy, Check, PenLine } from 'lucide-react';
import SujetDocsButton from '../../components/custom/SujetDocsButton';
import PdfPreviewModal from './PdfPreviewModal';

const ORIGIN_NAMES = { PDF: 'Document', AI_PROMPT: 'IA', JSON_IMPORT: 'Import', EMPTY: 'Vierge', COPY: 'Copie' };
function describeLineage(raw) {
    if (!raw) return null;
    let chain;
    try { chain = typeof raw === 'string' ? JSON.parse(raw) : raw; } catch { return null; }
    if (!Array.isArray(chain) || chain.length === 0) return null;
    const copyHops = chain.filter(h => h && h.t === 'COPY');
    if (copyHops.length === 0) return null;
    const root = chain[0];
    const rootOrigin = root && root.t !== 'COPY'
        ? `${ORIGIN_NAMES[root.t] || root.t}${root.label ? ` « ${root.label} »` : ''}`
        : null;
    const path = [];
    if (root && root.by && root.by.prenom) path.push(root.by.prenom);
    copyHops.forEach(h => { if (h.to && h.to.prenom) path.push(h.to.prenom); });
    return { depth: copyHops.length, rootOrigin, path };
}

// Historique des générations d'une thématique perso (sujets racines + enrichissements).
export default function MaThematiqueGenerations({ thematiqueId }) {
    const { sousSujets, fetchList, deleteSousSujet, resubmitJob, setActiveThematique, setPromptPrefill, setEnrichmentTarget, clearEnrichmentTarget } = useCustomStore();
    const [previewOpen, setPreviewOpen] = useState(false);
    const [previewSrc, setPreviewSrc] = useState(null);
    const [previewTitle, setPreviewTitle] = useState('');
    const [copiedPromptId, setCopiedPromptId] = useState(null);

    // Copie les consignes IA d'un sujet dans le presse-papier (feedback 2s)
    const copyPrompt = async (item) => {
        try {
            await navigator.clipboard.writeText(item.generation_prompt);
            setCopiedPromptId(item.id);
            setTimeout(() => setCopiedPromptId((cur) => (cur === item.id ? null : cur)), 2000);
        } catch {
            window.prompt('Copiez le prompt :', item.generation_prompt);
        }
    };

    useEffect(() => {
        // Set activeThematiqueId pour que fetchList() sans args (notamment après delete) fonctionne
        if (thematiqueId) setActiveThematique(thematiqueId);
        fetchList(thematiqueId);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [thematiqueId]);

    const openServerPreview = (pdfPath, pdfName) => {
        setPreviewSrc(pdfPath);
        setPreviewTitle(pdfName || 'Document');
        setPreviewOpen(true);
    };

    // Statut "humain" d'un enrichissement
    const enrichmentStatusLabel = (status) => {
        switch (status) {
            case 'PENDING':    return 'En attente';
            case 'GENERATING': return 'Génération…';
            case 'READY':      return 'Ajouté';
            case 'FAILED':     return 'Échec';
            default:           return status;
        }
    };
    const enrichmentStatusColor = (status) => {
        switch (status) {
            case 'PENDING':    return '#94a3b8';
            case 'GENERATING': return '#f59e0b';
            case 'READY':      return '#22c55e';
            case 'FAILED':     return '#ef4444';
            default:           return '#94a3b8';
        }
    };

    // Formate une date ISO en "DD MMM YYYY" (court) — pour traçabilité des actions
    const fmtDate = (iso) => {
        if (!iso) return '';
        try {
            const d = new Date(iso.replace(' ', 'T'));
            return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric' });
        } catch { return iso; }
    };

    // Détermine le badge "type d'action" pour un sujet racine
    const rootTypeBadge = (item) => {
        const origin = item.origin_type
            || (item.pdf_path ? 'PDF' : item.ai_provider ? 'AI_PROMPT' : 'EMPTY');
        if (origin === 'PDF' && item.nb_documents > 1) return { label: 'Multi-docs', color: '#8B5CF6' };
        if (origin === 'PDF')        return { label: 'Document', color: '#3B82F6' };
        if (origin === 'AI_PROMPT')  return { label: 'IA prompt', color: '#A855F7' };
        if (origin === 'JSON_IMPORT') return { label: 'Import JSON', color: '#6366F1' };
        if (origin === 'COPY')       return { label: 'Copie', color: '#64748B' };
        return { label: 'Manuel', color: '#94A3B8' };
    };

    // Détermine le badge "type d'enrichissement" depuis son origin_label
    const enrichTypeBadge = (enr) => {
        const lbl = (enr.origin_label || '').toLowerCase();
        if (lbl.includes('enrichissement ia') || lbl.includes('(prompt)')) return { label: 'IA prompt', color: '#A855F7' };
        if (lbl.includes('compilation') || lbl.includes('docs)'))           return { label: 'Multi-docs', color: '#8B5CF6' };
        return { label: 'Document', color: '#3B82F6' };
    };

    if (sousSujets.length === 0) {
        return (
            <div className="mt-generations-empty">
                <ContentIllustration name="file-text" size={64} />
                <p>Aucune génération pour le moment.<br />Uploadez un document ci-dessus pour commencer.</p>
            </div>
        );
    }

    return (
        <>
            <div className="mt-subjects-list">
                {sousSujets.map(item => {
                    const isPending = item.status === 'PENDING' || item.status === 'GENERATING';
                    const isFailed = item.status === 'FAILED';
                    // Sujet SUPPRIMÉ par l'utilisateur (delete.php → status FAILED +
                    // error_message "Supprimé…"). La suppression reste la suppression :
                    // pas de "Resoumettre" (on ré-uploade le document pour régénérer).
                    const isUserDeleted = isFailed && /supprim/i.test(item.error_message || '');

                    const StatusIcon = isPending
                        ? (item.status === 'GENERATING' ? Loader2 : Clock)
                        : isFailed ? AlertTriangle : CheckCircle;

                    const statusColor = isPending ? '#f59e0b' : isFailed ? '#ef4444' : '#22c55e';
                    const errorMsg = item.error_message || 'Erreur de génération';
                    const statusText = item.status === 'GENERATING' ? 'Génération en cours...'
                        : item.status === 'PENDING' ? 'QCM bientôt disponible'
                        : isFailed ? (errorMsg.length > 60 ? errorMsg.substring(0, 60) + '…' : errorMsg)
                        : `${item.nb_questions_active || item.nb_questions_generated || 0} questions`;

                    return (
                        <div key={item.id} className={`mt-subject-card mt-subject-card--${item.status.toLowerCase()}`}>
                            <div className="mt-subject-card__icon">
                                <ContentIllustration name="file-text" size={48} />
                            </div>
                            <div className="mt-subject-card__body">
                                <div className="mt-subject-card__title">
                                    <strong>{item.sous_sujet_label}</strong>
                                    <span className="mt-subject-card__status" style={{ color: statusColor }}>
                                        <StatusIcon size={14} className={item.status === 'GENERATING' ? 'spin' : ''} />
                                        {statusText}
                                    </span>
                                </div>
                                {item.sous_sujet_description && (
                                    <p className="mt-subject-card__desc">{item.sous_sujet_description}</p>
                                )}
                                <span className="mt-subject-card__meta" style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                                    {(() => {
                                        const badge = rootTypeBadge(item);
                                        return (
                                            <span style={{
                                                display: 'inline-flex', alignItems: 'center', gap: 4,
                                                padding: '2px 8px', borderRadius: 10,
                                                background: `${badge.color}1A`, color: badge.color,
                                                fontSize: '0.78em', fontWeight: 600,
                                            }}>
                                                {badge.label}
                                            </span>
                                        );
                                    })()}
                                    {(() => {
                                        const origin = item.origin_type
                                            || (item.pdf_path ? 'PDF' : item.ai_provider ? 'AI_PROMPT' : 'EMPTY');
                                        if (origin === 'PDF' && item.nb_documents > 1) {
                                            return <span>{item.origin_label || `${item.nb_documents} documents`}</span>;
                                        }
                                        if (origin === 'PDF') {
                                            return <span>{item.origin_label || item.pdf_original_name}{item.pdf_size_bytes ? ` — ${(item.pdf_size_bytes / 1048576).toFixed(1)} Mo` : ''}</span>;
                                        }
                                        if (origin === 'JSON_IMPORT') return <span>{item.origin_label || 'Import JSON'}</span>;
                                        if (item.origin_label) return <span style={{ fontStyle: 'italic' }}>{item.origin_label}</span>;
                                        return null;
                                    })()}
                                    {item.created_at && (
                                        <span style={{ color: 'var(--text-muted)', fontSize: '0.85em' }}>
                                            · {fmtDate(item.created_at)}
                                        </span>
                                    )}
                                    {item.generation_time_seconds > 0 && (
                                        <span style={{ color: 'var(--text-muted)', fontSize: '0.85em' }}>
                                            · {item.generation_time_seconds}s
                                        </span>
                                    )}
                                    <span style={{ color: 'var(--text-muted)', fontSize: '0.85em' }}>
                                        · {item.nb_questions_active || item.nb_questions_generated || 0}/50 questions
                                    </span>
                                </span>
                                {(() => {
                                    const lin = describeLineage(item.lineage);
                                    if (!lin) return null;
                                    return (
                                        <div style={{ marginTop: 4, fontSize: '0.78em', color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 5, flexWrap: 'wrap' }}>
                                            <Share2 size={11} />
                                            <span>Provenance :</span>
                                            {lin.rootOrigin && <span>{lin.rootOrigin}</span>}
                                            {lin.path.length > 0 && <span style={{ fontWeight: 600 }}>{lin.path.join(' → ')}</span>}
                                            <span>· copie niveau {lin.depth}</span>
                                        </div>
                                    );
                                })()}
                            </div>
                            <div className="mt-subject-card__actions">
                                {isFailed && !isUserDeleted && (
                                    <button className="btn btn-sm btn-primary"
                                        onClick={async () => {
                                            try { await resubmitJob(item.id, thematiqueId); }
                                            catch (e) { alert(e.message); }
                                        }}
                                        title="Relancer la génération"
                                    >
                                        <RefreshCw size={14} /> Resoumettre
                                    </button>
                                )}
                                {item.documents && item.documents.length > 0
                                    ? <SujetDocsButton documents={item.documents} />
                                    : (item.pdf_path && (
                                        <button className="btn btn-sm btn-outline" onClick={() => openServerPreview(item.pdf_path, item.pdf_original_name)}>
                                            <Eye size={14} /> PDF
                                        </button>
                                    ))}
                                {item.origin_type === 'AI_PROMPT' && item.generation_prompt && (
                                    <>
                                        <button className="btn btn-sm btn-outline"
                                            onClick={() => copyPrompt(item)}
                                            title="Copier les consignes IA de ce sujet"
                                        >
                                            {copiedPromptId === item.id ? <><Check size={14} /> Copié !</> : <><Copy size={14} /> Prompt</>}
                                        </button>
                                        <button className="btn btn-sm btn-outline"
                                            onClick={() => {
                                                clearEnrichmentTarget(); // racine = nouveau sujet, pas un enrichissement
                                                setPromptPrefill({
                                                    label: item.sous_sujet_label || '',
                                                    consignes: item.generation_prompt,
                                                });
                                            }}
                                            title="Rouvrir le formulaire Génération IA pré-rempli avec ces consignes (pour corriger et régénérer un nouveau sujet)"
                                        >
                                            <PenLine size={14} /> Modifier &amp; régénérer
                                        </button>
                                    </>
                                )}
                                <button
                                    className="btn btn-sm btn-ghost"
                                    onClick={() => { if (window.confirm('Supprimer ce sujet et ses questions ?')) deleteSousSujet(item.id); }}
                                    title="Supprimer"
                                >
                                    <Trash2 size={14} />
                                </button>
                            </div>

                            {/* Section enrichissements (childs) — affichée si au moins un enrichissement */}
                            {item.enrichments && item.enrichments.length > 0 && (
                                <div className="mt-subject-card__enrichments" style={{
                                    marginTop: 10, paddingTop: 10,
                                    borderTop: '1px dashed var(--card-border, #e5e7eb)',
                                }}>
                                    <div style={{ fontSize: '0.78em', color: 'var(--text-muted)', marginBottom: 6, fontWeight: 600 }}>
                                        <Layers size={11} style={{ verticalAlign: 'middle', marginRight: 4 }} />
                                        {item.enrichments.length} enrichissement{item.enrichments.length > 1 ? 's' : ''}
                                    </div>
                                    {item.enrichments.map(enr => {
                                        const eBadge = enrichTypeBadge(enr);
                                        return (
                                            <div key={enr.id} style={{
                                                display: 'flex', alignItems: 'center', gap: 10,
                                                padding: '6px 10px', marginTop: 4,
                                                background: 'var(--surface, #f9fafb)', borderRadius: 6,
                                                fontSize: '0.82em',
                                            }}>
                                                <Plus size={12} style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
                                                <div style={{ flex: 1, minWidth: 0 }}>
                                                    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
                                                        <span style={{
                                                            display: 'inline-flex', alignItems: 'center',
                                                            padding: '1px 7px', borderRadius: 10,
                                                            background: `${eBadge.color}1A`, color: eBadge.color,
                                                            fontSize: '0.88em', fontWeight: 600,
                                                            flexShrink: 0,
                                                        }}>
                                                            {eBadge.label}
                                                        </span>
                                                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', minWidth: 0 }}>
                                                            {enr.origin_label || 'Ajout'}
                                                        </span>
                                                        {enr.nb_questions_generated > 0 && (
                                                            <span style={{ color: 'var(--text-muted)' }}>
                                                                +{enr.nb_questions_generated} Q
                                                            </span>
                                                        )}
                                                        {enr.created_at && (
                                                            <span style={{ color: 'var(--text-muted)' }}>
                                                                · {fmtDate(enr.created_at)}
                                                            </span>
                                                        )}
                                                    </div>
                                                    <div style={{ fontSize: '0.85em', color: enrichmentStatusColor(enr.status) }}>
                                                        {enrichmentStatusLabel(enr.status)}
                                                        {enr.error_message && enr.status === 'FAILED' && (
                                                            <span title={enr.error_message}> · {enr.error_message.substring(0, 40)}{enr.error_message.length > 40 ? '…' : ''}</span>
                                                        )}
                                                    </div>
                                                </div>
                                                {enr.generation_consignes && (
                                                    <>
                                                        <button
                                                            onClick={() => copyPrompt({ id: enr.id, generation_prompt: enr.generation_consignes })}
                                                            title="Copier les consignes IA de cet enrichissement"
                                                            style={{ background: 'transparent', border: 'none', color: copiedPromptId === enr.id ? '#10b981' : 'var(--text-muted)', cursor: 'pointer', padding: 4 }}
                                                        >
                                                            {copiedPromptId === enr.id ? <Check size={12} /> : <Copy size={12} />}
                                                        </button>
                                                        <button
                                                            onClick={() => {
                                                                setEnrichmentTarget({ id: item.id, label: item.sous_sujet_label });
                                                                setPromptPrefill({ label: '', consignes: enr.generation_consignes });
                                                            }}
                                                            title="Ré-enrichir ce sujet avec ces consignes (modifiables avant génération)"
                                                            style={{ background: 'transparent', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', padding: 4 }}
                                                        >
                                                            <PenLine size={12} />
                                                        </button>
                                                    </>
                                                )}
                                                <button
                                                    onClick={() => {
                                                        if (window.confirm(`Supprimer cet enrichissement et ses questions ?`)) deleteSousSujet(enr.id);
                                                    }}
                                                    title="Supprimer cet enrichissement"
                                                    style={{ background: 'transparent', border: 'none', color: 'var(--text-muted)', cursor: 'pointer', padding: 4 }}
                                                >
                                                    <Trash2 size={12} />
                                                </button>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            {previewOpen && previewSrc && (
                <PdfPreviewModal src={previewSrc} title={previewTitle} onClose={() => setPreviewOpen(false)} />
            )}
        </>
    );
}
